import { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import AOS from "aos";
import "aos/dist/aos.css";

import Context from "../context/AppContext";
import Menu from "../components/layout/Menu";
import Footer from "../components/layout/Footer";

const TBC_NETWORK = {
  CHAIN_ID: 10791,
  CHAIN_ID_HEX: "0x2a27",
  NAME: "Trust Bitcoin Mainnet",
  SYMBOL: "TBC",
  RPC: "https://rpc.trustbitcoin.io",
  EXPLORER: "https://scan.trustbitcoin.io",
};

const LoginPage = () => {
  const { user, onConnect } = useContext(Context);

  const navigate = useNavigate();
  const location = useLocation();

  const from =
    location.state?.from?.pathname || "/dashboard";

  const [connecting, setConnecting] =
    useState(false);

  const [networkMessage, setNetworkMessage] =
    useState("");

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  useEffect(() => {
    if (user) {
      navigate(from, {
        replace: true,
      });
    }
  }, [from, navigate, user]);

  // ------------------------------------------
  // NORMALIZE CHAIN ID
  // ------------------------------------------

  const normalizeChainId = (chainId) => {
    try {
      if (chainId === null || chainId === undefined) {
        return null;
      }

      const value =
        String(chainId).trim();

      if (!value) {
        return null;
      }

      // WalletConnect may sometimes return:
      // eip155:10791
      if (value.includes(":")) {
        const parts =
          value.split(":");

        return Number(
          parts[parts.length - 1]
        );
      }

      // Hex chain id
      if (/^0x[0-9a-f]+$/i.test(value)) {
        return parseInt(value, 16);
      }

      return Number(value);
    } catch (error) {
      return null;
    }
  };

  // ------------------------------------------
  // WAIT UNTIL WALLET ACTUALLY SWITCHES
  // Useful especially for mobile wallets
  // ------------------------------------------

  const waitForTBCNetwork = async (
    walletProvider,
    timeout = 10000
  ) => {
    const startedAt = Date.now();

    while (
      Date.now() - startedAt < timeout
    ) {
      try {
        const chainId =
          await walletProvider.request({
            method: "eth_chainId",
          });

        const normalized =
          normalizeChainId(chainId);

        if (
          normalized ===
          TBC_NETWORK.CHAIN_ID
        ) {
          return true;
        }
      } catch (error) {
        console.warn(
          "Waiting for network switch:",
          error
        );
      }

      await new Promise((resolve) =>
        setTimeout(resolve, 500)
      );
    }

    return false;
  };

  // ------------------------------------------
  // ENSURE TRUST BITCOIN MAINNET
  // ------------------------------------------

  const ensureTrustBitcoinNetwork =
    async (walletProvider) => {
      if (
        !walletProvider ||
        typeof walletProvider.request !==
          "function"
      ) {
        throw new Error(
          "Wallet provider was not found."
        );
      }

      setNetworkMessage(
        "Checking Trust Bitcoin Mainnet..."
      );

      // First check existing network
      const currentChainId =
        await walletProvider.request({
          method: "eth_chainId",
        });

      const normalizedChainId =
        normalizeChainId(currentChainId);

      console.log(
        "Current wallet chain:",
        currentChainId,
        normalizedChainId
      );

      // Already correct
      if (
        normalizedChainId ===
        TBC_NETWORK.CHAIN_ID
      ) {
        setNetworkMessage(
          "Trust Bitcoin Mainnet connected."
        );

        return true;
      }

      // ------------------------------------------
      // TRY SWITCHING
      // ------------------------------------------

      setNetworkMessage(
        "Switching to Trust Bitcoin Mainnet..."
      );

      try {
        await walletProvider.request({
          method:
            "wallet_switchEthereumChain",

          params: [
            {
              chainId:
                TBC_NETWORK.CHAIN_ID_HEX,
            },
          ],
        });
      } catch (switchError) {
        console.log(
          "Switch network error:",
          switchError
        );

        const errorCode = Number(
          switchError?.code ||
            switchError?.data
              ?.originalError?.code
        );

        const errorMessage = String(
          switchError?.message || ""
        );

        // ------------------------------------------
        // USER REJECTED
        // ------------------------------------------

        if (
          errorCode === 4001 ||
          /user rejected/i.test(
            errorMessage
          ) ||
          /user denied/i.test(
            errorMessage
          )
        ) {
          throw new Error(
            "Network switch was cancelled. Please select Trust Bitcoin Mainnet to continue."
          );
        }

        // ------------------------------------------
        // REQUEST ALREADY PENDING
        // ------------------------------------------

        if (errorCode === -32002) {
          throw new Error(
            "A wallet request is already pending. Please open your wallet and approve the Trust Bitcoin Mainnet request."
          );
        }

        // ------------------------------------------
        // NETWORK DOESN'T EXIST
        // ADD TRUST BITCOIN MAINNET
        // ------------------------------------------

        const shouldAddNetwork =
          errorCode === 4902 ||
          /unknown chain/i.test(
            errorMessage
          ) ||
          /unrecognized chain/i.test(
            errorMessage
          ) ||
          /not added/i.test(
            errorMessage
          ) ||
          /does not exist/i.test(
            errorMessage
          );

        if (!shouldAddNetwork) {
          throw switchError;
        }

        setNetworkMessage(
          "Adding Trust Bitcoin Mainnet to your wallet..."
        );

        try {
          await walletProvider.request({
            method:
              "wallet_addEthereumChain",

            params: [
              {
                chainId:
                  TBC_NETWORK.CHAIN_ID_HEX,

                chainName:
                  TBC_NETWORK.NAME,

                nativeCurrency: {
                  name:
                    TBC_NETWORK.SYMBOL,

                  symbol:
                    TBC_NETWORK.SYMBOL,

                  decimals: 18,
                },

                rpcUrls: [
                  TBC_NETWORK.RPC,
                ],

                blockExplorerUrls: [
                  TBC_NETWORK.EXPLORER,
                ],
              },
            ],
          });
        } catch (addError) {
          const addCode = Number(
            addError?.code ||
              addError?.data
                ?.originalError?.code
          );

          if (addCode === 4001) {
            throw new Error(
              "Trust Bitcoin Mainnet was not added because the request was cancelled."
            );
          }

          throw new Error(
            addError?.message ||
              "Unable to add Trust Bitcoin Mainnet to your wallet."
          );
        }

        // ------------------------------------------
        // AFTER ADDING, SWITCH AGAIN
        // ------------------------------------------

        setNetworkMessage(
          "Selecting Trust Bitcoin Mainnet..."
        );

        await walletProvider.request({
          method:
            "wallet_switchEthereumChain",

          params: [
            {
              chainId:
                TBC_NETWORK.CHAIN_ID_HEX,
            },
          ],
        });
      }

      // ------------------------------------------
      // DON'T CHECK IMMEDIATELY ONLY ONCE
      // MOBILE WALLETS CAN TAKE SOME TIME
      // ------------------------------------------

      const switched =
        await waitForTBCNetwork(
          walletProvider
        );

      if (!switched) {
        throw new Error(
          "Trust Bitcoin Mainnet could not be selected. Please open your wallet, select Trust Bitcoin Mainnet, and try again."
        );
      }

      // ------------------------------------------
      // FINAL SECURITY CHECK
      // ------------------------------------------

      const finalChainId =
        await walletProvider.request({
          method: "eth_chainId",
        });

      const finalNormalized =
        normalizeChainId(finalChainId);

      console.log(
        "Final wallet network:",
        finalChainId,
        finalNormalized
      );

      if (
        finalNormalized !==
        TBC_NETWORK.CHAIN_ID
      ) {
        throw new Error(
          "Trust Bitcoin Mainnet must be selected before signing in."
        );
      }

      setNetworkMessage(
        "Trust Bitcoin Mainnet connected."
      );

      return true;
    };

  // ------------------------------------------
  // CONNECT + NETWORK CHECK + LOGIN
  // ------------------------------------------

  const handleConnectAndLogin =
    async () => {
      if (connecting) {
        return;
      }

      try {
        setConnecting(true);
        setNetworkMessage(
          "Connecting wallet..."
        );

        /*
         * For injected wallets such as:
         * MetaMask mobile browser
         * MetaMask extension
         * Trust-compatible injected wallets
         */
        const walletProvider =
          window?.ethereum;

        if (
          walletProvider &&
          typeof walletProvider.request ===
            "function"
        ) {
          // ------------------------------------------
          // CONNECT ACCOUNT FIRST
          // ------------------------------------------

          await walletProvider.request({
            method:
              "eth_requestAccounts",
          });

          // ------------------------------------------
          // MUST BE TRUST BITCOIN MAINNET
          // BEFORE LOGIN/SIGNATURE
          // ------------------------------------------

          await ensureTrustBitcoinNetwork(
            walletProvider
          );

          // ------------------------------------------
          // FINAL CHECK AGAIN
          // ------------------------------------------

          const chainId =
            await walletProvider.request({
              method: "eth_chainId",
            });

          if (
            normalizeChainId(chainId) !==
            TBC_NETWORK.CHAIN_ID
          ) {
            throw new Error(
              "Trust Bitcoin Mainnet must be selected before signing in."
            );
          }

          setNetworkMessage(
            "Network verified. Preparing login..."
          );
        }

        /*
         * Now call your existing wallet/login flow.
         *
         * IMPORTANT:
         * Ideally AppContext/onConnect should ALSO
         * perform the same final network validation,
         * especially for WalletConnect providers.
         */
        await onConnect();

        setNetworkMessage("");
      } catch (error) {
        console.error(
          "Wallet/network login error:",
          error
        );

        setNetworkMessage("");

        const message =
          error?.message ||
          "Unable to connect your wallet.";

        // Using existing SweetAlert style if available
        if (window.swal) {
          window.swal(
            "Wallet Connection Required",
            message,
            "error"
          );
        } else {
          alert(message);
        }
      } finally {
        setConnecting(false);
      }
    };

  return (
    <>
      <Menu />

      <section
        className="signSec sliderSec padSec overHide pb-md-5"
        id="top"
      >
        <div className="container-fluid">
          <div className="sliderBg">
            <div className="el"></div>

            <div className="text-center mb-3">
              <h2 className="text-white">
                Trust Bitcoin Travel Portal
              </h2>
            </div>

            <div className="row justify-content-center">
              <div className="col-md-4">

                <div
                  className="signHere"
                  data-aos="fade-up"
                  data-aos-delay="300"
                  data-aos-duration="300"
                >
                  <div className="text-center">

                    <div className="striph1 text-center locktag mb-3">
                      <i className="fa-solid fa-lock"></i>
                      {" "}
                      URL Verification:{" "}
                      <b>
                        <span className="greenText">
                          https://
                        </span>
                        mytrip.trustbitcoin.io/
                      </b>
                    </div>

                    <h4>Sign In</h4>

                  </div>

                  <div>
                    <div className="">

                      <p className="text-center text-white mb-3">
                        Connect your wallet and sign the login message.
                        No gas fee or blockchain transaction is required.
                      </p>

                      {/* NETWORK STATUS */}
                      {networkMessage && (
                        <p
                          className="text-center mb-2"
                          style={{
                            color: "#f8bd2a",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          <i className="fa-solid fa-spinner fa-spin me-2"></i>
                          {networkMessage}
                        </p>
                      )}

                      <button
                        type="button"
                        disabled={connecting}
                        className="mt-2 mybtn d-flex gap-2 align-items-center w-100 m-auto"
                        onClick={
                          handleConnectAndLogin
                        }
                        style={{
                          opacity:
                            connecting
                              ? 0.7
                              : 1,

                          cursor:
                            connecting
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        <div className="bgIN">

                          {connecting ? (
                            <>
                              {/* <i className="fa-solid fa-spinner fa-spin me-2"></i> */}
                              Checking Network...
                            </>
                          ) : (
                            "Connect Wallet & Sign In"
                          )}

                        </div>
                      </button>

                    </div>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default LoginPage;